import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { BillableMeterV2 } from '../api/types';
import { Badge, Alert } from './ui';

interface Props {
  meter: BillableMeterV2;
  /** Other configured meters of the same utility — populates the Copy-from picker. */
  candidateSources?: BillableMeterV2[];
  onClose: () => void;
  onSaved: () => void;
}

interface CachedReading {
  value: unknown;
  read_at: string;
}

const NONE_OPT = '__none__';

export default function MeterConfigModal({ meter, candidateSources = [], onClose, onSaved }: Props) {
  const [registers, setRegisters] = useState<string[]>([]);
  const [peak, setPeak] = useState<string>(NONE_OPT);
  const [standard, setStandard] = useState<string>(NONE_OPT);
  const [offPeak, setOffPeak] = useState<string>(NONE_OPT);
  const [md, setMd] = useState<string>(NONE_OPT);
  const [readings, setReadings] = useState<Record<string, CachedReading>>({});
  // PR 1d — Meter Capacity & NMD state.
  const [breakerAmps, setBreakerAmps] = useState<string>('');
  const [breakerPhases, setBreakerPhases] = useState<1 | 3>(3);
  const [voltageClass, setVoltageClass] = useState<'LV' | 'MV' | ''>('');
  const [voltageValue, setVoltageValue] = useState<string>('');
  const [nmdOverride, setNmdOverride] = useState<string>('');
  const [mdIsMeasured, setMdIsMeasured] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [copySource, setCopySource] = useState<string>('');
  const [copying, setCopying] = useState(false);
  const [calibrating, setCalibrating] = useState(false);
  const [calibrationProposal, setCalibrationProposal] = useState<{
    peak?: string;
    standard?: string;
    off_peak?: string;
    confidences: Record<string, number>;
  } | null>(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meter.meter_asset_id]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [regRes, cfgRes] = await Promise.all([
        api.listMeterRegisters(meter.meter_asset_id),
        api.getMeterTouConfig(meter.meter_asset_id),
      ]);
      setRegisters(regRes.data.registers);
      const cfg = cfgRes.data;
      setPeak(cfg?.tou_peak_register ?? NONE_OPT);
      setStandard(cfg?.tou_standard_register ?? NONE_OPT);
      setOffPeak(cfg?.tou_offpeak_register ?? NONE_OPT);
      setMd(cfg?.md_register ?? NONE_OPT);
      if (cfg?.tou_register_readings) {
        try {
          setReadings(JSON.parse(cfg.tou_register_readings));
        } catch { /* ignore malformed */ }
      }
      // PR 1d — Meter Capacity & NMD load.
      setBreakerAmps(cfg?.breaker_size_amps != null ? String(cfg.breaker_size_amps) : '');
      setBreakerPhases((cfg?.breaker_phases === 1 ? 1 : 3));
      setVoltageClass(cfg?.voltage_class ?? '');
      setVoltageValue(cfg?.voltage_value != null ? String(cfg.voltage_value) : '');
      setNmdOverride(cfg?.nmd_kva != null ? String(cfg.nmd_kva) : '');
      setMdIsMeasured(cfg?.md_is_measured ?? true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load register config');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      // Pull all configured + currently-selected so cache covers everything visible
      const wanted = new Set<string>();
      for (const r of [peak, standard, offPeak, md]) if (r !== NONE_OPT) wanted.add(r);
      const res = await api.refreshMeterRegisters(meter.meter_asset_id, Array.from(wanted));
      setReadings(res.data.readings);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const save = async () => {
    // Client-side duplicate check
    const chosen = [peak, standard, offPeak].filter((v) => v !== NONE_OPT);
    if (new Set(chosen).size !== chosen.length) {
      setError('Peak / Standard / Off-Peak must reference distinct registers.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const breakerAmpsNum = breakerAmps.trim() === '' ? null : Number(breakerAmps);
      const voltageValueNum = voltageValue.trim() === '' ? null : Number(voltageValue);
      const nmdOverrideNum = nmdOverride.trim() === '' ? null : Number(nmdOverride);
      // Validate numeric inputs client-side so the API isn't called with NaN.
      if (breakerAmps.trim() !== '' && (!isFinite(breakerAmpsNum!) || breakerAmpsNum! <= 0)) {
        setError('Breaker size must be a positive number');
        setSaving(false);
        return;
      }
      if (voltageValue.trim() !== '' && (!isFinite(voltageValueNum!) || voltageValueNum! <= 0)) {
        setError('Voltage must be a positive number');
        setSaving(false);
        return;
      }
      if (nmdOverride.trim() !== '' && (!isFinite(nmdOverrideNum!) || nmdOverrideNum! <= 0)) {
        setError('NMD override must be a positive number');
        setSaving(false);
        return;
      }
      if (voltageClass === 'MV' && voltageValueNum == null && nmdOverrideNum == null) {
        setError('MV class requires a voltage or an NMD override');
        setSaving(false);
        return;
      }
      await api.putMeterTouConfig(meter.meter_asset_id, {
        tou_peak_register:     peak     === NONE_OPT ? null : peak,
        tou_standard_register: standard === NONE_OPT ? null : standard,
        tou_offpeak_register:  offPeak  === NONE_OPT ? null : offPeak,
        md_register:           md       === NONE_OPT ? null : md,
        breaker_size_amps:     breakerAmpsNum,
        breaker_phases:        breakerPhases,
        voltage_class:         voltageClass === '' ? null : voltageClass,
        voltage_value:         voltageValueNum,
        nmd_kva:               nmdOverrideNum,
        md_is_measured:        mdIsMeasured,
      });
      setSavedAt(Date.now());
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const copyFrom = async () => {
    if (!copySource) return;
    try {
      setCopying(true);
      setError(null);
      const res = await api.copyMeterTouConfig(meter.meter_asset_id, copySource);
      const cfg = res.data;
      setPeak(cfg.tou_peak_register ?? NONE_OPT);
      setStandard(cfg.tou_standard_register ?? NONE_OPT);
      setOffPeak(cfg.tou_offpeak_register ?? NONE_OPT);
      setMd(cfg.md_register ?? NONE_OPT);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Copy failed');
    } finally {
      setCopying(false);
    }
  };

  const calibrate = async () => {
    try {
      setCalibrating(true);
      setError(null);
      const res = await api.calibrateMeterRegisters(meter.meter_asset_id);
      if (res.data.proposal) {
        setCalibrationProposal(res.data.proposal);
      } else {
        setError(res.data.reason || 'Calibration produced no proposal');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Calibration failed');
    } finally {
      setCalibrating(false);
    }
  };

  const acceptCalibration = () => {
    if (!calibrationProposal) return;
    if (calibrationProposal.peak)     setPeak(calibrationProposal.peak);
    if (calibrationProposal.standard) setStandard(calibrationProposal.standard);
    if (calibrationProposal.off_peak) setOffPeak(calibrationProposal.off_peak);
    setCalibrationProposal(null);
  };

  const formatReading = (regName: string) => {
    if (regName === NONE_OPT) return null;
    const r = readings[regName];
    if (!r) return <span className="text-content-muted italic">never read</span>;
    const v = typeof r.value === 'number' ? r.value.toLocaleString('en-ZA', { maximumFractionDigits: 2 }) : String(r.value);
    const when = new Date(r.read_at).toLocaleString('en-ZA');
    return <span className="text-content">{v} <span className="text-content-muted">· {when}</span></span>;
  };

  const isWater = meter.meter_type === 'GroundwaterSensorAsset';

  // Mirror of server-side computeNmdKva (bill-calculator) for the live
  // "Computed NMD" hint. Source of truth lives on the server; this is purely
  // display assistance and must stay in sync with the server formula.
  const computedNmdHint = (() => {
    const amps = Number(breakerAmps);
    if (!isFinite(amps) || amps <= 0) return null;
    if (voltageClass === 'LV') {
      const v = voltageValue.trim() === '' ? 230 : Number(voltageValue);
      if (!isFinite(v) || v <= 0) return null;
      const kva = (amps * v * breakerPhases) / 1000;
      return { kva, basis: `LV ${amps}A × ${v}V × ${breakerPhases} phases` };
    }
    if (voltageClass === 'MV') {
      const v = Number(voltageValue);
      if (!isFinite(v) || v <= 0) return null;
      const kva = (amps * v * Math.sqrt(3)) / 1000;
      return { kva, basis: `MV ${amps}A × ${v}V × √3` };
    }
    return null;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/* Plain bg-surface-raised div, not <Card> — matches the modal
          precedent from TariffManagement/Tenants/Pockets (fixed inset-0
          overlays keep their own rounded-xl/shadow-2xl rather than
          inheriting Card's rounded-lg). */}
      <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-border-token flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-content">Configure Meter</h2>
            <p className="text-sm text-content-muted mt-0.5">{meter.meter_name}</p>
          </div>
          <button onClick={onClose} className="text-content-muted hover:text-content text-2xl leading-none">&times;</button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-content-muted">Loading...</div>
        ) : isWater ? (
          <div className="p-8 text-center text-content-muted">
            <p>TOU register configuration applies to electricity meters only.</p>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {error && <Alert tone="danger">{error}</Alert>}

            <div>
              <p className="text-sm text-content-muted mb-3">
                Map the meter's named registers to billing buckets. When all three TOU registers are set,
                their deltas become authoritative totals and intervals are used for HS/LS season split + cross-check.
                Leave a slot as <em>None</em> to fall back to calculation from interval data.
              </p>
              {registers.length === 0 && (
                <Alert tone="warning">
                  No register attributes found on this meter in OpenRemote. Has the meter been onboarded?
                </Alert>
              )}
            </div>

            {/* Copy-from + Calibrate row */}
            {(candidateSources.length > 0 || registers.length > 0) && (
              <div className="bg-surface-sunken border border-border-token rounded-lg p-3 space-y-3">
                {candidateSources.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-content-muted whitespace-nowrap">Copy config from:</label>
                    <select
                      value={copySource}
                      onChange={(e) => setCopySource(e.target.value)}
                      className="flex-1 border border-border-token rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">— Select source meter —</option>
                      {candidateSources.map((m) => (
                        <option key={m.meter_asset_id} value={m.meter_asset_id}>{m.meter_name}</option>
                      ))}
                    </select>
                    <button
                      onClick={copyFrom}
                      disabled={!copySource || copying}
                      className="text-xs px-3 py-1.5 border border-border-token rounded-md hover:bg-surface-raised disabled:opacity-50"
                    >
                      {copying ? 'Copying…' : 'Copy'}
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={calibrate}
                    disabled={calibrating}
                    className="text-xs px-3 py-1.5 border border-border-token rounded-md hover:bg-surface-raised disabled:opacity-50"
                  >
                    {calibrating ? 'Analysing 7 days of data…' : 'Calibrate from data'}
                  </button>
                  <span className="text-xs text-content-muted">
                    Walks recent intervals to propose which register matches each bucket.
                  </span>
                </div>
                {calibrationProposal && (
                  // Not routed through <Card>: border-amber-300 is a
                  // deliberate highlight colour (this box flags a pending
                  // proposal), not a neutral border-gray-* shell — Card
                  // would force it to border-border-token and erase that
                  // signal. bg-white -> bg-surface-raised only.
                  // border-amber-300 stays legible in dark unchanged:
                  // contrastRatio('#fcd34d', surfaceRaised dark #292524) =
                  // 10.5:1, computed by hand.
                  <div className="bg-surface-raised border border-amber-300 rounded-md p-3 text-xs space-y-1">
                    <div className="font-semibold text-content">Proposed mapping</div>
                    {(['peak', 'standard', 'off_peak'] as const).map((b) => (
                      <div key={b} className="flex justify-between">
                        <span className="capitalize">{b.replace('_', '-')}</span>
                        <span className="font-mono">
                          {calibrationProposal[b] ?? '—'}
                          {calibrationProposal[b] && (
                            <span className="text-content-muted ml-1">
                              ({Math.round((calibrationProposal.confidences[b] ?? 0) * 100)}% confidence)
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <button onClick={acceptCalibration} className="px-2 py-1 bg-accent text-accent-fg rounded">Apply</button>
                      <button onClick={() => setCalibrationProposal(null)} className="px-2 py-1 border border-border-token rounded">Dismiss</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Meter Capacity & NMD — PR 1d.
                Drives the calculator's three-way Utilised Capacity computation
                (NMD vs MUC vs AUC) for NCC / TNC / Urban-LV / GCC / Excess NCC. */}
            <section>
              <h3 className="text-sm font-semibold text-content mb-2">Meter Capacity & NMD</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    id="md-is-measured"
                    type="checkbox"
                    checked={mdIsMeasured}
                    onChange={(e) => setMdIsMeasured(e.target.checked)}
                    className="rounded border-border-token text-accent"
                  />
                  <label htmlFor="md-is-measured" className="text-sm text-content">
                    MD measured (uncheck for Businessrate / Landrate / Homepower Std — NMD selects the bracket only)
                  </label>
                </div>

                <div className="grid grid-cols-[140px_140px_140px] gap-3">
                  <div>
                    <label className="block text-xs text-content-muted mb-1">Breaker (A)</label>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={breakerAmps}
                      onChange={(e) => setBreakerAmps(e.target.value)}
                      placeholder="e.g. 80"
                      className="w-full border border-border-token rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-content-muted mb-1">Phases</label>
                    <select
                      value={breakerPhases}
                      onChange={(e) => setBreakerPhases(e.target.value === '1' ? 1 : 3)}
                      className="w-full border border-border-token rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={1}>1-phase</option>
                      <option value={3}>3-phase</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-content-muted mb-1">Voltage class</label>
                    <select
                      value={voltageClass}
                      onChange={(e) => setVoltageClass(e.target.value as 'LV' | 'MV' | '')}
                      className="w-full border border-border-token rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">— None —</option>
                      <option value="LV">LV (low)</option>
                      <option value="MV">MV (medium)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-[1fr_1fr] gap-3">
                  <div>
                    <label className="block text-xs text-content-muted mb-1">
                      Voltage (V) <span className="text-content-muted">{voltageClass === 'LV' ? '— default 230' : voltageClass === 'MV' ? '— required' : ''}</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={voltageValue}
                      onChange={(e) => setVoltageValue(e.target.value)}
                      placeholder={voltageClass === 'MV' ? 'e.g. 11000' : voltageClass === 'LV' ? '230 (default)' : ''}
                      className="w-full border border-border-token rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-content-muted mb-1">
                      NMD override (kVA) <span className="text-content-muted">— optional</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={nmdOverride}
                      onChange={(e) => setNmdOverride(e.target.value)}
                      placeholder={computedNmdHint ? computedNmdHint.kva.toFixed(2) : 'computed from breaker × voltage'}
                      className="w-full border border-border-token rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {computedNmdHint && (
                  <div className="text-xs text-content-muted bg-surface-sunken border border-border-token rounded-md p-2">
                    <span className="font-semibold">Computed NMD:</span> {computedNmdHint.kva.toFixed(2)} kVA
                    <span className="text-content-muted ml-1">({computedNmdHint.basis})</span>
                    {nmdOverride.trim() !== '' && (
                      // text-amber-700 (the same hex as the warning token's
                      // base) is 3.94:1 against surface-sunken dark
                      // (#0c0a09) — fails AA. dark:text-amber-400 is
                      // 11.84:1, computed by hand — passes comfortably.
                      <span className="text-amber-700 dark:text-amber-400 ml-2">— override active: {Number(nmdOverride).toFixed(2)} kVA</span>
                    )}
                  </div>
                )}
                <p className="text-xs text-content-muted">
                  Eskom Schedule §2.1 + reviewed NERSA NMD/MEC Rules. NMD is the contracted capacity per POD.
                  Override takes precedence; otherwise NMD is derived from breaker × voltage × phases.
                </p>
              </div>
            </section>

            {/* TOU Registers */}
            <section className={meter.is_virtual ? 'opacity-50 pointer-events-none' : ''}>
              <h3 className="text-sm font-semibold text-content mb-2 flex items-center gap-2">
                TOU Registers
                {meter.is_virtual && (
                  // Was bg-purple-50 text-purple-700 — shade-normalized onto
                  // Badge's purple tone (bg-purple-100/900 text-purple-900/100),
                  // the same 50/700->100/900 normalization TariffManagement
                  // already applied to its own status pills.
                  <Badge tone="purple" title="Virtual meters have no physical registers; TOU bucketing is computed from source meters' 30-min intervals before billing.">
                    disabled (virtual meter)
                  </Badge>
                )}
              </h3>
              <div className="space-y-2">
                {([
                  { label: 'Peak',     value: peak,     set: setPeak },
                  { label: 'Standard', value: standard, set: setStandard },
                  { label: 'Off-Peak', value: offPeak,  set: setOffPeak },
                ] as const).map(({ label, value, set }) => (
                  <div key={label} className="grid grid-cols-[100px_180px_1fr] items-center gap-3">
                    <label className="text-sm text-content">{label}</label>
                    <select
                      value={value}
                      onChange={(e) => set(e.target.value)}
                      className="border border-border-token rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={NONE_OPT}>None (calculate)</option>
                      {registers.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <div className="text-xs">{formatReading(value)}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* MD Register */}
            <section>
              <h3 className="text-sm font-semibold text-content mb-2">Maximum Demand Register</h3>
              <div className="grid grid-cols-[100px_180px_1fr] items-center gap-3">
                <label className="text-sm text-content">Max kVA</label>
                <select
                  value={md}
                  onChange={(e) => setMd(e.target.value)}
                  className="border border-border-token rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={NONE_OPT}>None (calculate)</option>
                  {registers.filter((r) => /^MD\d+$/i.test(r)).map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <div className="text-xs">{formatReading(md)}</div>
              </div>
              <p className="mt-2 text-xs text-content-muted">
                When set, the meter's MD register is cross-checked against the interval-computed MD
                (restricted to Standard/Peak windows). Discrepancies are flagged on the bill.
              </p>
            </section>

            <div className="flex items-center justify-between pt-2 border-t border-border-token">
              <button
                onClick={refresh}
                disabled={refreshing}
                className="text-sm px-3 py-1.5 border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-50"
              >
                {refreshing ? 'Refreshing...' : 'Refresh readings'}
              </button>
              <div className="flex items-center gap-2">
                {/* Final tidy: text-green-600 is 4.60:1 against
                    surface-raised dark — a technical pass this effort
                    treats as fragile (same "Saved" indicator shape as
                    MeterConfig.tsx's I1/I2). That page's byte-identical
                    indicator was hardened to dark:text-green-400 (8.71:1);
                    this modal's copy was missed until now — matching it
                    here for the same reason. */}
                {savedAt && <span className="text-xs text-green-600 dark:text-green-400">Saved</span>}
                <button onClick={onClose} className="text-sm px-3 py-1.5 border border-border-token rounded-md hover:bg-surface-sunken">Cancel</button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="text-sm px-3 py-1.5 bg-accent text-accent-fg rounded-md hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
